import { styled } from '@mui/material/styles';
import SearchTextField from '@/components/SearchTextField/SearchTextField';
import Button from '@/components/Button/Button';
import TrashIcon from '@/assets/trash.svg?react';
import EditIcon from '@/assets/edit.svg?react';
import PlusIcon from '@/assets/plus.svg?react';
import FilterIcon from '@/assets/filter.svg?react';
import { FormattedMessage } from 'react-intl';
import { ReactNode, useRef, useState } from 'react';
import Popper from '@/components/Popper/Popper';
import { flattenDeep } from 'lodash';
import { FilterSchema } from '@/components/ResourcesSidebar/ResourcesSidebarHelpers';

const TableActions = ({
  searchText,
  onSearchTextChange,
  filters,
  filtersValues,
  onAddClick,
  addLabel,
  onMassUpdateClick,
  massUpdateDisabled,
  onDeleteClick,
  deleteDisabled,
  onMassImportClick,
  loading = false,
  massImportDisabled,
  addDisabled,
}: {
  searchText: string;
  onSearchTextChange: (text: string) => void;
  filters?: ReactNode;
  filtersValues?: Partial<FilterSchema>;
  onAddClick?: () => void;
  addLabel?: ReactNode;
  onMassUpdateClick?: () => void;
  massUpdateDisabled?: boolean;
  onDeleteClick?: () => void;
  deleteDisabled?: boolean;
  onMassImportClick?: () => void;
  loading?: boolean;
  massImportDisabled?: boolean;
  addDisabled?: boolean;
}) => {
  const filterRef = useRef<HTMLButtonElement>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const showNotification =
    flattenDeep<
      | boolean
      | string
      | number
      | boolean
      | string[]
      | number[]
      | [string, string]
    >(Object.values(filtersValues || {})).length > 0;

  return (
    <Root>
      <StyledSearchTextField
        value={searchText}
        onChange={onSearchTextChange}
        placeholder={
          <FormattedMessage
            id="filterAsYouType"
            defaultMessage="Filter as you type"
          />
        }
        small={false}
      />
      <TableActionButtons>
        {onDeleteClick ? (
          <Button
            variant="outlined"
            disabled={deleteDisabled || loading}
            onClick={onDeleteClick}
            aria-label="Delete Button"
          >
            <TrashIcon />
            <FormattedMessage id="delete" defaultMessage="Delete" />
          </Button>
        ) : null}
        {onMassUpdateClick ? (
          <Button
            variant="outlined"
            onClick={onMassUpdateClick}
            disabled={massUpdateDisabled || loading}
            aria-label="Mass Update Button"
          >
            <EditIcon />
            <FormattedMessage
              id="massUpdate"
              defaultMessage="Mass Update"
            />
          </Button>
        ) : null}
        {onMassImportClick ? (
          <Button
            variant="outlined"
            onClick={onMassImportClick}
            disabled={loading || massImportDisabled}
            aria-label="Mass Import Button"
          >
            <PlusIcon />
            <FormattedMessage
              id="massImport"
              defaultMessage="Mass Import"
            />
          </Button>
        ) : null}
        {addLabel ? (
          <Button
            variant="contained"
            onClick={onAddClick}
            disabled={loading || addDisabled}
            aria-label="Add Button"
          >
            <PlusIcon />
            {addLabel}
          </Button>
        ) : null}
        {filters ? (
          <Button
            aria-label="Filter Button"
            variant="outlined"
            icon
            ref={filterRef}
            onClick={() => setFiltersOpen((v) => !v)}
            disabled={loading}
          >
            <FilterIcon />
            {showNotification && <FilterNotification />}
          </Button>
        ) : null}
        {filters ? (
          <Popper
            aria-label="Filters Popper"
            open={filtersOpen}
            anchorEl={filterRef.current}
            onClose={() => setFiltersOpen(false)}
          >
            {filters}
          </Popper>
        ) : null}
      </TableActionButtons>
    </Root>
  );
};

const Root = styled('div')({
  display: 'flex',
  margin: '0px 40px',
  padding: '8px 0 16px',
});

const StyledSearchTextField = styled(SearchTextField)({
  width: 250,
  margin: 0,
});

const TableActionButtons = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  flexGrow: 1,
  justifyContent: 'flex-end',
}));

const FilterNotification = styled('div')(({ theme }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  position: 'absolute',
  top: -4,
  right: -4,
}));

export default TableActions;
